import {SignerTransaction} from "@tinymanorg/tinyman-js-sdk";
import {useCallback, useEffect, useState} from "react";

import useInitiateSignCeremony from "../core/util/hook/useInitiateSignCeremony";
import {MINUTE_IN_MS} from "../core/util/time/timeConstants";
import {ApplicationToSwapWidgetMessage} from "../core/widgetTypes";
import {
  getIsParentSigner,
  useWalletContext
} from "../wallet/util/context/WalletContext.utils";
import {encodeTxnsInTxGroups} from "../core/util/transaction/transactionUtils";
import {getParentUrlFromSearchParams, sendMessageToParentApp} from "../core/widgetUtils";

/**
 * Amount of time (in ms) to be waited for a response from the parent
 * application after sending a sign request
 */
// eslint-disable-next-line no-magic-numbers
const TXN_WAIT_TIMEOUT = 2 * MINUTE_IN_MS;

export type SignerState =
  | {
      /** Signer is idle, waiting for an action */
      status: "idle";
    }
  | {
      /** Waiting for txn to be signed by the user */
      status: "pending";
    }
  | {
      status: "success";
      signedTxns: Uint8Array[];
    }
  | {
      status: "error";
      error: unknown;
    };

/**
 * Helper hook to sign a transaction regardless of the signing method;
 * - If the signing method is `ParentSigner`, it will send a message to the parent application, and wait
 *   for the response (or timeout)
 * - If the signing method is `LocalSigner`, it will use the `signerCallback` function
 */
export function useTxnSign() {
  const [signerState, setSignerState] = useState<SignerState>({
    status: "idle"
  });
  const resetSignerState = () => setSignerState({status: "idle"});
  const isWaitingForSign = signerState.status === "pending";
  const {walletType, account} = useWalletContext();
  const isParentSigner = getIsParentSigner(walletType);
  const {signerCallback} = useInitiateSignCeremony();
  const parentUrl = getParentUrlFromSearchParams();
  const accountAddress = account?.address;

  const runSign = useCallback(
    async (txGroups: SignerTransaction[][]) => {
      try {
        if (!accountAddress) {
          throw new Error("No account address found");
        }

        setSignerState({
          status: "pending"
        });

        if (isParentSigner) {
          sendMessageToParentApp(
            {
              message: {
                type: "TXN_SIGN_REQUEST",
                txGroups: encodeTxnsInTxGroups(txGroups)
              }
            },
            parentUrl
          );
        } else {
          const signedTxns = await signerCallback(txGroups);

          setSignerState({
            status: "success",
            signedTxns
          });
        }
      } catch (e) {
        setSignerState({
          status: "error",
          error: e
        });
      }
    },
    [accountAddress, isParentSigner, parentUrl, signerCallback]
  );

  /**
   * Handles messages from parent application
   */
  const messageHandler = useCallback(
    (event: MessageEvent<ApplicationToSwapWidgetMessage>) => {
      // We only accept messages from the parent application
      if (event.source !== window.parent) {
        return;
      }

      switch (event.data.message?.type) {
        case "TXN_SIGN_RESPONSE": {
          if (!isWaitingForSign) {
            console.warn(
              "Unexpected sign response received from the parent application. Ignoring."
            );
            return;
          }

          const {signedTxns} = event.data.message;

          setSignerState({
            status: "success",
            signedTxns
          });
          break;
        }

        case "FAILED_TXN_SIGN": {
          if (!isWaitingForSign) {
            console.warn(
              "Unexpected sign error message received from the parent application. Ignoring."
            );
            return;
          }
          setSignerState({
            status: "error",
            error:
              event.data.message.error ||
              new Error("The parent app failed to sign the transaction")
          });
          break;
        }

        default: {
          // ignore unknown messages
          break;
        }
      }
    },
    [isWaitingForSign]
  );

  /**
   * Add message listeners
   */
  // @ts-ignore - Not all paths return a value, but it's intended
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    // We don't need `postMessage` api for local signer
    if (isParentSigner) {
      window.addEventListener("message", messageHandler, false);

      return () => {
        window.removeEventListener("message", messageHandler, false);
      };
    }
  }, [isParentSigner, messageHandler]);

  /**
   * Reset signer state after a threshold time
   */
  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (isWaitingForSign) {
      timeout = setTimeout(() => {
        resetSignerState();
        sendMessageToParentApp(
          {
            message: {type: "TXN_SIGN_REQUEST_TIMEOUT"}
          },
          parentUrl
        );
        // TODO: We can remove this log once we handle this case on UI
        console.warn("Timeout for signing, cancelling...");
      }, TXN_WAIT_TIMEOUT);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [isWaitingForSign, parentUrl]);

  return {
    signerState,
    runSign,
    resetSignerState
  };
}
